import type { FC } from "react"
import { useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"

import { PriceStrategy } from "@/domains/thick-v1/stores/price-strategies"
import {
  MAX_TICK,
  MIN_TICK,
  tickToPriceString,
  tryParseTickFromPrice,
} from "@/domains/manual-cl-core"
import { detectOrientation } from "@/domains/thick-v1/components/create-position/price-history-composite"
import type { PoolOhlcStore } from "@/domains/thick-v1/stores/pool-ohlc-store"

/**
 * The Uniswap SDK's `tickMath` invariant rejects values at the extreme
 * boundaries even though our `MIN_TICK`/`MAX_TICK` constants are the
 * inclusive endpoints. Clamp one step inside to keep label formatting safe
 * when the store reports full-range selections.
 */
const SAFE_TICK_MARGIN = 1

import type { CreatePositionStore } from "../../stores/create-position-store"
import type { ThickV1TickDistributionStore } from "../../tick-distribution"
import type { RangeBrushDomain } from "./range-brush-helpers"
import { RangeBrushView } from "./range-brush-view"

export interface CreatePositionRangeBrushCompositeProps {
  createPositionStore: CreatePositionStore
  tickDistributionStore: ThickV1TickDistributionStore
  /**
   * Optional OHLC store used to draw subtle dashed reference lines at the
   * historical low/high for the currently-selected price-history window. When
   * omitted (e.g. Storybook, isolated composite tests) the brush simply
   * renders without them.
   */
  poolOhlcStore?: PoolOhlcStore
  className?: string
  height?: number
}

/**
 * The only observer layer joining `CreatePositionStore` with
 * `ThickV1TickDistributionStore` to drive `RangeBrushView`.
 *
 * Drag interactions call `setMinTick` / `setMaxTick` on the position store,
 * which already snaps to `tickSpacing` internally. A drag is treated as an
 * explicit user override so we clear `selectedStrategy` (switch the preset row
 * to "Custom") and unset `fullRange` — matching the rule in §9.1 of the plan.
 *
 * The price-orientation toggle lives on `CreatePositionStore` so the brush
 * pills and the card min/max rows render the same unit.
 */
/**
 * Zoom step + clamps. Multiplying/dividing padding by 1.6 per click gives
 * the user roughly five clicks from fit-to-data (`1`) to either bound —
 * enough resolution for typical tick distributions without letting the view
 * collapse to a single bin or explode to the full tick space.
 */
const ZOOM_STEP = 1.6
const ZOOM_MIN = 0.2
const ZOOM_MAX = 8
const MIN_PADDING_SPANS = 50
const MIN_HANDLE_GUTTER_RATIO = 0.12

export const CreatePositionRangeBrushComposite: FC<CreatePositionRangeBrushCompositeProps> =
  observer(({ createPositionStore, tickDistributionStore, poolOhlcStore, className, height }) => {
    const [zoomFactor, setZoomFactor] = useState(1)

    const pool = createPositionStore.pool
    const invert = createPositionStore.isPriceRangeInverted
    const tickSpacing = createPositionStore.tickSpacing
    const currentTick = createPositionStore.currentTick
    const [rawMinTick, rawMaxTick] = createPositionStore.priceRangeCalculation.ticks

    const canUseZoomFactor = useCallback(
      (nextZoomFactor: number): boolean => {
        const window = computeBrushWindow({
          tickSpacing,
          rawMinTick,
          rawMaxTick,
          currentTick,
          zoomFactor: nextZoomFactor,
        })
        if (!window || rawMinTick === undefined || rawMaxTick === undefined) return true
        const width = window.tickUpper - window.tickLower
        if (width <= 0) return false
        const leftGutter = (rawMinTick - window.tickLower) / width
        const rightGutter = (window.tickUpper - rawMaxTick) / width
        return leftGutter >= MIN_HANDLE_GUTTER_RATIO && rightGutter >= MIN_HANDLE_GUTTER_RATIO
      },
      [currentTick, rawMaxTick, rawMinTick, tickSpacing],
    )

    const handleZoomIn = useCallback(() => {
      setZoomFactor((prev) => {
        const next = Math.max(ZOOM_MIN, prev / ZOOM_STEP)
        return canUseZoomFactor(next) ? next : prev
      })
    }, [canUseZoomFactor])
    const handleZoomOut = useCallback(() => {
      setZoomFactor((prev) => Math.min(ZOOM_MAX, prev * ZOOM_STEP))
    }, [])

    const brushWindow = computeBrushWindow({
      tickSpacing,
      rawMinTick,
      rawMaxTick,
      currentTick,
      zoomFactor,
    })

    // Keep the tick-distribution viewport aligned to the proposed range but
    // pad it aggressively so the user has room to drag the handles without
    // the selection band immediately filling the chart. Earlier iterations
    // set the window to exactly `[minTick, maxTick]`, which meant that a
    // tight preset (±3 ticks) produced a viewport identical to the selection
    // — the handles landed at the chart edges and the whole chart looked
    // like a solid selection band. Padding to `2×` the selection width (with
    // a ±50 tickSpacing floor) keeps the selection at roughly a third of
    // the chart width across every preset, which is the single biggest
    // driver of "slider feels draggable" vs "slider feels sticky".
    //
    // `zoomFactor` scales that padding: <1 zooms in (tighter viewport, the
    // selection fills more of the chart), >1 zooms out (selection shrinks
    // relative to the visible tick range).
    useEffect(() => {
      if (!brushWindow) {
        tickDistributionStore.setWindow(undefined, undefined)
        return
      }
      tickDistributionStore.setWindow(brushWindow.tickLower, brushWindow.tickUpper)
    }, [tickDistributionStore, brushWindow?.tickLower, brushWindow?.tickUpper])

    const handleMinTickChange = useCallback(
      (tick: number) => {
        if (!createPositionStore.pool) return
        createPositionStore.setFullRange(false)
        createPositionStore.setSelectedStrategy(undefined)
        createPositionStore.setMinTick(tick)
      },
      [createPositionStore],
    )

    const handleMaxTickChange = useCallback(
      (tick: number) => {
        if (!createPositionStore.pool) return
        createPositionStore.setFullRange(false)
        createPositionStore.setSelectedStrategy(undefined)
        createPositionStore.setMaxTick(tick)
      },
      [createPositionStore],
    )

    /**
     * Parse a user-typed price string (in whatever unit the pill currently
     * shows — i.e. respecting `invert`) into a tick and delegate to
     * `setMinTick` / `setMaxTick`. The store setters own clamping +
     * tickSpacing snap + strategy clearing + deposit-mode reconcile, so this
     * callback stays a thin translator.
     *
     * `priceToTick` (from `manual-cl-core`) is the only place that knows how
     * to interpret `invert` correctly against token decimals; duplicating it
     * here would drift out of sync with the `tickToPriceString` round-trip
     * used for `formatTickPrice`.
     */
    const applyPriceEdit = useCallback(
      (handle: "min" | "max", rawInput: string) => {
        const target = createPositionStore.pool
        if (!target || !tickSpacing) return
        const tick = tryParseTickFromPrice({
          price: rawInput,
          token0: target.token0,
          token1: target.token1,
          tickSpacing,
          invert,
        })
        if (tick === undefined) return
        createPositionStore.setFullRange(false)
        createPositionStore.setSelectedStrategy(undefined)
        if (handle === "min") {
          createPositionStore.setMinTick(tick)
        } else {
          createPositionStore.setMaxTick(tick)
        }
      },
      [createPositionStore, invert, tickSpacing],
    )

    const handleMinPriceCommit = useCallback(
      (rawInput: string) => applyPriceEdit("min", rawInput),
      [applyPriceEdit],
    )

    const handleMaxPriceCommit = useCallback(
      (rawInput: string) => applyPriceEdit("max", rawInput),
      [applyPriceEdit],
    )

    if (!pool || !tickSpacing) {
      return (
        <RangeBrushView
          bins={[]}
          currentTick={undefined}
          tickSpacing={1}
          minTick={0}
          maxTick={0}
          baseSymbol="—"
          quoteSymbol="—"
          isLoading
          readOnly
          className={className}
          height={height}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          canZoomIn={false}
          canZoomOut={false}
        />
      )
    }

    const token0 = pool.token0
    const token1 = pool.token1

    const formatTickPrice = (tick: number): string => {
      const safeTick = Math.min(
        MAX_TICK - SAFE_TICK_MARGIN,
        Math.max(MIN_TICK + SAFE_TICK_MARGIN, tick),
      )
      try {
        return tickToPriceString({ tick: safeTick, token0, token1, invert, decimalPlaces: 6 })
      } catch {
        return "—"
      }
    }

    const minTick = rawMinTick ?? currentTick ?? 0
    const maxTick = rawMaxTick ?? currentTick ?? 0

    // Historical reference ticks for the current OHLC window. We reconcile
    // orientation the same way the price-history chart does — the indexer
    // may emit OHLC prices in either `token1/token0` or `token0/token1`
    // orientation per-pool, but `priceToTick` only makes sense in canonical
    // `token1/token0` (the tick domain is orientation-invariant, but the
    // *input price* must be expressed in canonical orientation for the
    // conversion to land on the right tick). We detect which way the close
    // values lean by comparing to the canonical current price, then pass
    // the matching `invert` flag into `tryParseTickFromPrice`.
    //
    // When the OHLC series comes back in token0/token1 orientation,
    // what was the "low" price is actually the "high" tick and vice-versa
    // (because inverting swaps the ordering). We swap the pair back so the
    // "low tick" / "high tick" labels stay honest regardless of upstream
    // orientation.
    const windowPriceRange = poolOhlcStore?.windowPriceRange
    const ohlcCloseSeries =
      poolOhlcStore?.points
        .map((point) => point.close ?? point.open)
        .filter((value): value is number => value !== undefined && Number.isFinite(value)) ?? []
    const ohlcOrientation = detectOrientation({
      closeValues: ohlcCloseSeries,
      referenceValue: createPositionStore.currentPrice,
    })
    const needsInvertForTick = ohlcOrientation === "inverted"
    const parseHistoricalTick = (price: number): number | undefined =>
      tryParseTickFromPrice({
        price: String(price),
        token0,
        token1,
        tickSpacing,
        invert: needsInvertForTick,
      })
    const lowTickRaw =
      windowPriceRange !== undefined ? parseHistoricalTick(windowPriceRange.low) : undefined
    const highTickRaw =
      windowPriceRange !== undefined ? parseHistoricalTick(windowPriceRange.high) : undefined
    const [historicalLowTick, historicalHighTick] =
      lowTickRaw !== undefined && highTickRaw !== undefined && lowTickRaw > highTickRaw
        ? [highTickRaw, lowTickRaw]
        : [lowTickRaw, highTickRaw]
    const historicalWindowLabel = windowPriceRange ? poolOhlcStore?.window : undefined

    return (
      <RangeBrushView
        bins={tickDistributionStore.binsInWindow}
        currentTick={currentTick}
        tickSpacing={tickSpacing}
        minTick={minTick}
        maxTick={maxTick}
        baseSymbol={invert ? token1.symbol : token0.symbol}
        quoteSymbol={invert ? token0.symbol : token1.symbol}
        formatTickPrice={formatTickPrice}
        invert={invert}
        onInvertToggle={() => createPositionStore.togglePriceRangeInversion()}
        onMinTickChange={handleMinTickChange}
        onMaxTickChange={handleMaxTickChange}
        onMinPriceCommit={handleMinPriceCommit}
        onMaxPriceCommit={handleMaxPriceCommit}
        isLoading={tickDistributionStore.isLoading && tickDistributionStore.bins.length === 0}
        readOnly={createPositionStore.fullRange && createPositionStore.selectedStrategy === PriceStrategy.FULL_RANGE}
        className={className}
        height={height}
        visibleDomain={brushWindow}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        canZoomIn={zoomFactor > ZOOM_MIN && canUseZoomFactor(Math.max(ZOOM_MIN, zoomFactor / ZOOM_STEP))}
        canZoomOut={zoomFactor < ZOOM_MAX}
        historicalLowTick={historicalLowTick}
        historicalHighTick={historicalHighTick}
        historicalWindowLabel={historicalWindowLabel}
      />
    )
  })

function computeBrushWindow({
  tickSpacing,
  rawMinTick,
  rawMaxTick,
  currentTick,
  zoomFactor,
}: {
  tickSpacing: number | undefined
  rawMinTick: number | undefined
  rawMaxTick: number | undefined
  currentTick: number | undefined
  zoomFactor: number
}): RangeBrushDomain | undefined {
  if (!tickSpacing) return undefined
  const floorPadding = tickSpacing * MIN_PADDING_SPANS
  if (rawMinTick !== undefined && rawMaxTick !== undefined) {
    const selectionRange = Math.max(rawMaxTick - rawMinTick, tickSpacing)
    const padding = Math.max(selectionRange, floorPadding) * zoomFactor
    return { tickLower: rawMinTick - padding, tickUpper: rawMaxTick + padding }
  }
  if (currentTick === undefined) return undefined
  const padding = floorPadding * zoomFactor
  return { tickLower: currentTick - padding, tickUpper: currentTick + padding }
}
